<template>
  <div class="create-job-post-step2 d-flex flex-column h-100">
    <div class="step-header pa-6 pb-4">
      <div class="d-flex align-center ga-3">
        <div>
          <h1 class="text-h1 text-textDarkest">
            Choose a Template
          </h1>
          <div class="text-body-2 text-textLight mt-1">
            Select an optional template to pre-fill your new job post's structure.
          </div>
        </div>
      </div>
    </div>

    <v-divider class="opacity-100" />

    <div class="step-body flex-grow-1 overflow-hidden">
      <v-row
        no-gutters
        class="h-100"
      >
        <!-- Left: template picker -->
        <v-col
          cols="5"
          class="border-e d-flex flex-column h-100"
        >
          <div
            id="templates-heading"
            class="pa-4 pb-2 text-caption text-textDark text-uppercase font-weight-medium"
          >
            Templates
          </div>

          <div
            v-if="store.templatesLoading"
            class="d-flex justify-center pa-6"
          >
            <v-progress-circular
              indeterminate
              color="brandPrimary"
              size="32"
              width="2"
            />
          </div>

          <div
            v-else-if="store.templates.length === 0"
            class="text-body-2 text-textLight text-center pa-6"
          >
            No templates available. You can still create a blank job post.
          </div>

          <div
            v-else
            class="pa-2 overflow-y-auto flex-grow-1"
            role="list"
            aria-labelledby="templates-heading"
          >
            <v-list-item
              role="listitem"
              :active="store.selectedTemplateId === null"
              color="brandPrimary"
              rounded="lg"
              :aria-current="store.selectedTemplateId === null ? 'true' : undefined"
              @click="clearTemplate"
            >
              <v-list-item-title class="text-h3 text-textDarkest">
                No template
              </v-list-item-title>
              <v-list-item-subtitle class="text-body-3 text-textLight opacity-100">
                Start with a blank job post
              </v-list-item-subtitle>
            </v-list-item>

            <v-list-item
              v-for="template in store.templates"
              :key="template.id"
              role="listitem"
              :active="store.selectedTemplateId === template.id"
              color="brandPrimary"
              rounded="lg"
              :aria-current="store.selectedTemplateId === template.id ? 'true' : undefined"
              @click="store.selectTemplate(template.id)"
            >
              <v-list-item-title class="text-h3 text-textDarkest">
                {{ template.title }}
              </v-list-item-title>
              <v-list-item-subtitle
                v-if="template.is_default"
                class="text-body-3 text-textLight opacity-100"
              >
                Default template
              </v-list-item-subtitle>
            </v-list-item>
          </div>
        </v-col>

        <!-- Right: combined preview -->
        <v-col
          cols="7"
          class="overflow-y-auto h-100"
        >
          <WizardJobPreview
            :job="store.mergedJobAndTemplatePreview ? {
              ...store.mergedJobAndTemplatePreview,
              title: formTitle,
              location: formLocation,
              job_code: formJobCode,
            } : null"
            :loading="store.templatePreviewLoading"
          >
            <template #empty>
              Select a template on the left to preview its content here.
            </template>
          </WizardJobPreview>
        </v-col>
      </v-row>
    </div>

    <v-divider class="opacity-100" />

    <div class="step-footer pa-6 d-flex ga-4">
      <v-btn
        class="h-100"
        variant="text"
        color="textDarkest"
        density="compact"
        prepend-icon="$arrow-left"
        @click="store.goToStep1"
      >
        Back
      </v-btn>

      <v-btn
        color="primary"
        variant="flat"
        rounded="lg"
        :loading="store.createLoading && !store.firstDraftLoading"
        :disabled="store.firstDraftLoading"
        @click="store.createJobPost(router)"
      >
        Create Job Post
      </v-btn>

      <FirstDraftButton
        v-if="authData.aiEnabled && !store.selectedJobId"
        :disabled="store.createLoading"
        :loading="store.firstDraftLoading"
        @click="onFirstDraftClick"
      >
        Create with First Draft
      </FirstDraftButton>
    </div>
  </div>
</template>

<script setup>
import { onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useCreateJobPostWizard } from '@/stores/jobPosts/createJobPostWizard';
import { useAppAuthData } from '@/stores/app/authData';
import { useTracking } from '@/stores/tracking/tracking';
import FirstDraftButton from '@ui/components/firstDraft/FirstDraftButton.vue';
import WizardJobPreview from './WizardJobPreview.vue';

const store = useCreateJobPostWizard();
const router = useRouter();
const { formTitle, formJobCode, formLocation } = storeToRefs(store);

const { authData } = storeToRefs(useAppAuthData());

const onFirstDraftClick = async () => {
  useTracking().trackName({ name: 'Clicked: Write with First Draft' });
  await store.createJobPost(router, { writeWithFirstDraft: true });
};

const clearTemplate = () => {
  store.selectedTemplateId = null;
  store.templatePreview = null;
};

onMounted(() => {
  store.loadTemplates();
});
</script>
